import { getAPIFullUrl, getAxiosConfig, handleApiError } from './risapi'; // Use correct path: ../../../api/risapi
import { risapi } from './risapi'; // Use correct path: ../../../api/risapi

/**
 * Fetches a list of opening balances, potentially with filters and pagination.
 * @param {object} filterParameters - Object containing parameters like paginate, page, per_page, search_term.
 * @param {function} callback - Function to call with the response data or error.
 */
export const getOpeningBalances = async (filterParameters, callback) => {
    try {
        const response = await risapi.get(getAPIFullUrl('/api/opening-balances', filterParameters), getAxiosConfig());
        // Check for HTTP success status codes (e.g., 200 OK)
        if (response.status >= 200 && response.status < 300) {
             callback(response.data); // Contains { opening_balances: [...] }
        } else {
             // Handle unexpected success statuses if necessary, otherwise treat as error
             console.warn("Unexpected success status:", response.status, response.data);
             callback({ error: { message: `Unexpected status: ${response.status}` } });
        }
    } catch (error) {
        // Use handleApiError for consistent error object structure if needed,
        // but often just passing the error back is sufficient for the caller.
        console.error("API Error in getOpeningBalances:", error);
        handleApiError(error); // This logs the error, alert might be undesirable here
        callback({ error }); // Pass error object back
    }
};

/**
 * Fetches a single opening balance entry by its ID.
 * @param {number|string} id - The ID of the opening balance entry.
 * @param {function} callback - Function to call with the response data or error.
 */
export const fetchOpeningBalance = async (id, callback) => {
    try {
        const response = await risapi.get(`/api/opening-balances/${id}`, getAxiosConfig());
        if (response.status === 200) {
            callback(response.data); // Contains { opening_balance: {...} }
        } else {
             console.warn("Unexpected success status:", response.status, response.data);
             callback({ error: { message: `Unexpected status: ${response.status}` } });
        }
    } catch (error) {
        console.error(`API Error in fetchOpeningBalance (ID: ${id}):`, error);
        handleApiError(error);
        callback({ error });
    }
};

/**
 * Creates a new opening balance entry.
 * @param {object} newData - The data for the new opening balance.
 * @param {function} callback - Function to call with the response data or error object (from axios).
 */
export const createOpeningBalance = async (newData, callback) => {
    try {
        // Opening balance data is simple enough for JSON
        const response = await risapi.post('/api/opening-balances', newData, getAxiosConfig());
        if (response.status === 201) { // 201 Created
            callback(response.data); // Contains { opening_balance: {...} }
        } else {
             console.warn("Unexpected success status on create:", response.status, response.data);
             callback({ error: { response } }); // Pass response within error object
        }
    } catch (error) {
        console.error("API Error in createOpeningBalance:", error);
        // Pass the entire error object back to the form's handleApiError
        callback({ error });
    }
};

/**
 * Updates an existing opening balance entry.
 * @param {object} editData - The data for the opening balance to update (must include id).
 * @param {function} callback - Function to call with the response data or error object.
 */
export const updateOpeningBalance = async (editData, callback) => {
    try {
        const response = await risapi.put(`/api/opening-balances/${editData.id}`, editData, getAxiosConfig());
        if (response.status === 200) { // 200 OK
            callback(response.data); // Contains { opening_balance: {...} }
        } else {
            console.warn("Unexpected success status on update:", response.status, response.data);
            callback({ error: { response } });
        }
    } catch (error) {
        console.error(`API Error in updateOpeningBalance (ID: ${editData.id}):`, error);
        // Pass the entire error object back to the form's handleApiError
        callback({ error });
    }
};

/**
 * Deletes an opening balance entry.
 * @param {object} itemToDelete - The opening balance object to delete (must include id).
 * @param {function} callback - Function to call with success indication or error object.
 */
export const destroyOpeningBalance = async (itemToDelete, callback) => {
    try {
        const response = await risapi.delete(`/api/opening-balances/${itemToDelete.id}`, getAxiosConfig());
        if (response.status === 204) { // 204 No Content
            callback({ opening_balance: itemToDelete }); // Indicate success by returning original item
        } else {
             console.warn("Unexpected success status on delete:", response.status, response.data);
             callback({ error: { response } });
        }
    } catch (error) {
        console.error(`API Error in destroyOpeningBalance (ID: ${itemToDelete.id}):`, error);
        // Pass the entire error object back to the list page's Swal handler
        callback({ error });
    }
};

// --- destroyOpeningBalancesMultiple function (if needed later) ---
/*
export const destroyOpeningBalancesMultiple = async (ids, callback) => {
    try {
        const sendData = { ids: ids, _method: 'delete' };
        const response = await risapi.post('/api/opening-balances/delete/multiple', sendData, getAxiosConfig());
        if (response.status === 204) {
            callback({ opening_balance: ids }); // Success
        } else {
             console.warn("Unexpected success status on multi-delete:", response.status, response.data);
             callback({ error: { response } });
        }
    } catch (error) {
        console.error("API Error in destroyOpeningBalancesMultiple:", error);
        callback({ error });
    }
};
*/